import { useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, CardContent, Link, MenuItem, menuItemClasses, Typography as T } from "@mui/material";
import AddReportModal from "datasets/pages/dashboards/components/AddReportModal";
import RemoveReportModal from "datasets/pages/dashboards/components/RemoveReportModal";
import Report from "datasets/pages/dashboards/types/interfaces/report";
import { DEFAULT_DATASET_ID } from "datasets/utils/dataset";
import { format } from "date-fns";
import yn from "yn";
import { ReportsIcon } from "~/assets/icons";
import ActionsMenu from "~/components/ActionsMenu";
import CT from "~/components/ClampedTypography";
import InteractiveCard, { IconContainer } from "~/components/InteractiveCard";
import useDatasetPermission from "~/pages/datasets/hooks/useDatasetPermission";

const { VITE_SIGMA_SELF_SERVICE_ENABLED } = import.meta.env;

const formatDate = (timestamp: string | number) => {
  const date = new Date(timestamp);
  return format(date, "MMM dd, yyyy");
};

interface ReportCardProps {
  report: Report;
}

const ReportCard = ({ report }: ReportCardProps) => {
  const { datasetId } = useParams();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const canEdit = useDatasetPermission("edit:catalog-report");
  const canRemove = canEdit && datasetId !== DEFAULT_DATASET_ID;
  const showPublishedStatus = yn(VITE_SIGMA_SELF_SERVICE_ENABLED) && canEdit;

  const handleEdit = () => {
    setShowActions(false);
    setEditModalOpen(true);
  };

  const handleRemove = () => {
    setShowActions(false);
    setRemoveModalOpen(true);
  };

  const menuOptions = useMemo(() => {
    const options = [
      <MenuItem key="edit" onClick={handleEdit}>
        Edit
      </MenuItem>,
    ];

    if (canRemove) {
      options.push(
        <MenuItem
          key="remove"
          sx={{
            color: "attention.main",
            [`&.${menuItemClasses.root}:hover`]: {
              color: "common.white",
              bgcolor: "attention.main",
            },
          }}
          onClick={handleRemove}
        >
          Remove
        </MenuItem>
      );
    }
    return options;
  }, [canRemove]);

  return (
    <>
      <Link
        component={RouterLink}
        data-testid="dataset-report-card"
        underline="none"
        to={report.id}
        sx={{ display: "flex" }}
      >
        <InteractiveCard
          onMouseEnter={() => setShowActions(true)}
          onMouseLeave={() => setShowActions(false)}
        >
          <CardContent sx={{ px: 3, pt: 2.5, gap: 1.25, height: 205 }}>
            <Box display="flex" alignItems="center" flex="0" justifyContent="space-between">
              <IconContainer
                sx={{
                  bgcolor: "primary.main",
                  color: "common.white",
                  borderRadius: "50%",
                  width: 34,
                  height: 34,
                  "& svg": {
                    width: 18,
                    height: 18,
                  },
                }}
              >
                <ReportsIcon />
              </IconContainer>
              {canEdit && (
                <Box position="absolute" right={12} top={12}>
                  <ActionsMenu
                    disablePortal
                    placement="bottom-start"
                    visible={showActions}
                    menuItems={menuOptions}
                  />
                </Box>
              )}
            </Box>
            <Box>
              {showPublishedStatus && (
                <T variant="label" fontSize={12} data-testid="report-published-status">
                  {report.published ? "PUBLISHED" : "DRAFT"}
                </T>
              )}
              <CT variant="body1" sx={{ lineHeight: 1.2, fontWeight: 500 }}>
                {report.displayName}
              </CT>
            </Box>
            {report.description && (
              <CT variant="body2" sx={{ opacity: 0.7 }}>
                {report.description}
              </CT>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: "auto",
              }}
            >
              <T variant="caption" sx={{ opacity: 0.7, fontSize: 14, lineHeight: 1.2 }}>
                Updated {formatDate(report.updatedAt)}
              </T>
            </Box>
          </CardContent>
        </InteractiveCard>
      </Link>
      <AddReportModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        report={report}
      />

      <RemoveReportModal
        open={removeModalOpen}
        onClose={() => setRemoveModalOpen(false)}
        report={report}
      />
    </>
  );
};

export default ReportCard;
